import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api.js";
import { Dialog } from "../ui/Dialog.js";
import type { ActionOutcome } from "../ui/ActionConfirmation.js";
import { NotifyToggle } from "./notify.js";
import {
  recurringLessons,
  type RecurrencePlanDetail,
  type RecurrencePlanSettingsInput,
} from "./recurrences.js";

type PendingSettings = {
  requested: RecurrencePlanSettingsInput;
  persisted: boolean;
};
function matches(
  detail: RecurrencePlanDetail,
  requested: RecurrencePlanSettingsInput,
) {
  return (
    detail.plan.notify_participants === requested.notify_participants &&
    detail.plan.internal_notes === requested.internal_notes
  );
}

export function EditRecurringLesson({
  planId,
  studentId,
  identity,
  onClose,
  onOutcome,
}: {
  planId: string;
  studentId?: string;
  identity: string;
  onClose: () => void;
  onOutcome: (outcome: ActionOutcome) => void;
}) {
  const [detail, setDetail] = useState<RecurrencePlanDetail | null>(null);
  const [settings, setSettings] = useState<RecurrencePlanSettingsInput | null>(
    null,
  );
  const [busyAction, setBusyAction] = useState<
    "load" | "save" | "check" | null
  >("load");
  const [problem, setProblem] = useState<string | null>(null);
  const [needsCheck, setNeedsCheck] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const pending = useRef<PendingSettings | null>(null);
  const writing = useRef(false);
  const alive = useRef(true);
  const busy = busyAction !== null;

  function accept(next: RecurrencePlanDetail, replaceDraft = false) {
    if (
      next?.plan?.id !== planId ||
      (studentId !== undefined && next.plan.portal_student_id !== studentId) ||
      typeof next.plan.notify_participants !== "boolean" ||
      typeof next.plan.internal_notes !== "string" ||
      typeof next.plan.updated_at !== "string" ||
      Number.isNaN(Date.parse(next.plan.updated_at))
    ) {
      throw new Error("The recurring lesson settings could not be confirmed.");
    }
    setDetail(next);
    if (replaceDraft)
      setSettings({
        notify_participants: next.plan.notify_participants,
        internal_notes: next.plan.internal_notes,
      });
  }

  useEffect(() => {
    alive.current = true;
    let current = true;
    recurringLessons
      .detail(planId)
      .then((next) => {
        if (current) accept(next, true);
      })
      .catch((cause: unknown) => {
        if (current)
          setProblem(
            cause instanceof Error
              ? cause.message
              : "We could not load the current settings.",
          );
      })
      .finally(() => {
        if (current) setBusyAction(null);
      });
    return () => {
      current = false;
      alive.current = false;
    };
  }, [planId, studentId]);

  function settled(next: RecurrencePlanDetail, discardDraft = false) {
    accept(next, discardDraft);
    setNeedsCheck(false);
    const attempted = pending.current;
    if (attempted && matches(next, attempted.requested)) {
      pending.current = null;
      setProblem(null);
      onOutcome({
        variant: "success",
        title: "Recurring lesson updated",
        description: identity,
        details: [
          {
            label: "Booking notifications",
            value: next.plan.notify_participants ? "On" : "Off",
          },
          {
            value:
              "Existing diary lessons and notifications already sent are unchanged.",
          },
        ],
      });
      onClose();
    } else if (attempted) {
      const message = attempted.persisted
        ? "The save succeeded, but the stored settings changed again. Your draft is kept; review the current values before saving again."
        : "The current settings were checked and do not confirm your changes. Your draft is kept; review the saved values before saving again.";
      setProblem(message);
      onOutcome({
        variant: "attention",
        title: "Recurring settings checked; review your draft",
        description: identity,
        details: [
          {
            label: "Current booking notifications",
            value: next.plan.notify_participants ? "On" : "Off",
          },
          { value: message },
        ],
      });
    } else setProblem(null);
  }

  async function check(discardDraft = false) {
    if (busy) return;
    setBusyAction("check");
    try {
      const fresh = await recurringLessons.detail(planId);
      if (!alive.current) return;
      settled(fresh, discardDraft || settings === null);
    } catch {
      if (alive.current) {
        setNeedsCheck(true);
        setProblem(
          "The current settings still need checking. Your draft is kept. Reload the authoritative plan before saving again.",
        );
      }
    } finally {
      if (alive.current) setBusyAction(null);
    }
  }

  async function save() {
    if (
      !settings ||
      writing.current ||
      needsCheck ||
      unsupported ||
      !editable ||
      !detail?.plan.updated_at
    )
      return;
    writing.current = true;
    setBusyAction("save");
    setProblem(null);
    const requested = {
      ...settings,
      internal_notes: settings.internal_notes.trim(),
      expected_updated_at: detail.plan.updated_at,
    };
    pending.current = { requested, persisted: false };
    let readAttempted = false;
    try {
      const result = await recurringLessons.updateSettings(planId, requested);
      if (result?.plan?.id !== planId || !matches(result, requested))
        throw new Error(
          "The returned plan does not confirm the requested settings.",
        );
      pending.current.persisted = true;
      readAttempted = true;
      settled(await recurringLessons.detail(planId));
    } catch (cause) {
      const unavailable =
        cause instanceof ApiError && [405, 501].includes(cause.status);
      const definiteFailure =
        !pending.current?.persisted &&
        cause instanceof ApiError &&
        [400, 401, 403, 404, 405, 409, 422, 501].includes(cause.status);
      if (definiteFailure) pending.current = null;
      setUnsupported(unavailable);
      setNeedsCheck(!definiteFailure);
      const message = unavailable
        ? "Editing recurring settings is not available on this API deployment. Your draft is kept."
        : definiteFailure
          ? cause.message
          : pending.current?.persisted
            ? "Your settings were saved, but the current plan could not be reloaded. Your draft is kept. Reload before saving again."
            : "Recurring lesson outcome needs checking. Your draft is kept. Reload the plan before saving again.";
      setProblem(message);
      onOutcome({
        variant: definiteFailure ? "attention" : "uncertain",
        title: unavailable
          ? "Recurring settings editor unavailable"
          : definiteFailure
            ? "Recurring lesson was not updated"
            : "Recurring lesson outcome needs checking",
        description: identity,
        details: [{ value: message }],
      });
      if (!readAttempted && !unavailable) {
        try {
          const fresh = await recurringLessons.detail(planId);
          accept(fresh);
          setNeedsCheck(false);
          // A failed validation refreshes the baseline without discarding the
          // draft or replacing its specific error. An ambiguous write can be
          // settled only by an authoritative read of the requested values.
          if (!definiteFailure) settled(fresh);
        } catch {
          setNeedsCheck(true);
        }
      }
    } finally {
      writing.current = false;
      setBusyAction(null);
    }
  }

  const editable =
    detail?.plan.status === "active" || detail?.plan.status === "paused";
  const draftDiffers = !!detail && !!settings && !matches(detail, settings);
  return (
    <Dialog
      title="Edit recurring lesson"
      description={identity}
      onClose={onClose}
      busy={busyAction === "save"}
    >
      {problem && (
        <div className="error" role="alert">
          {problem}
        </div>
      )}
      {busy && !detail && <p>Loading current settings…</p>}
      {detail && !editable && (
        <p>This plan is stopped or completed and cannot be edited.</p>
      )}
      {detail && (
        <p className="small">
          {detail.plan.cadence === "weekly" ? "Weekly" : "Fortnightly"} ·{" "}
          {new Date(`${detail.plan.starts_on}T12:00:00`).toLocaleDateString(
            "en-GB",
            { weekday: "long" },
          )}{" "}
          at {detail.plan.start_time.slice(0, 5)}
        </p>
      )}
      {settings && (
        <div className="stack">
          <p className="small muted">Edit booking notifications and internal notes here. To change the day, time or repeat pattern, stop this plan and create a replacement. Lessons already booked need to be reviewed separately.</p>
          <NotifyToggle
            checked={settings.notify_participants}
            disabled={busy || !editable || needsCheck || unsupported}
            onChange={(notify_participants) =>
              setSettings({ ...settings, notify_participants })
            }
            label="Email the student when this recurring plan creates new lessons"
          />
          <p className="small muted">
            This applies to lessons created after you save this setting.
            Existing diary lessons and notifications already sent are unchanged.
          </p>
          <label className="stack">
            Internal notes
            <textarea
              maxLength={2000}
              rows={5}
              value={settings.internal_notes}
              disabled={busy || !editable || needsCheck || unsupported}
              onChange={(event) =>
                setSettings({ ...settings, internal_notes: event.target.value })
              }
            />
          </label>
        </div>
      )}
      {problem && detail && draftDiffers && (
        <details>
          <summary>Current saved settings</summary>
          <p>
            Booking notifications:{" "}
            {detail.plan.notify_participants ? "On" : "Off"}
          </p>
          <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {detail.plan.internal_notes || "No internal notes"}
          </p>
        </details>
      )}
      <div className="row" style={{ marginTop: 16, flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={
            busy ||
            !editable ||
            !settings ||
            needsCheck ||
            unsupported ||
            settings.internal_notes.length > 2000
          }
          onClick={() => void save()}
        >
          {busyAction === "save" ? "Saving…" : "Save settings"}
        </button>
        {(needsCheck || (!detail && !busy)) && (
          <button type="button" disabled={busy} onClick={() => void check()}>
            Reload plan
          </button>
        )}
        {detail && settings && draftDiffers && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void check(true)}
          >
            Reload and discard draft
          </button>
        )}
        <button
          className="quiet"
          type="button"
          disabled={busyAction === "save"}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Dialog>
  );
}
